/**
 * Where this arche was opened from (ADR-037 D8), resolved for the host: the
 * forge page or the Arche Builder session a platform link came from
 * (`lib/arche-opener.ts`), read with the person's own session — so only their
 * own forge or session is ever named — plus how to go back there and how a
 * file the arche offers lands there.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  OPENER_PARAM,
  forgetOpener,
  parseOpener,
  recallOpener,
  rememberOpener,
  type OpenerKind,
  type OpenerRef,
} from '@/lib/arche-opener';

/** A file an arche offered, as the destination's route takes it. */
export interface OfferedFile {
  holderId: string | null;
  sourceArcheId: string;
  documentId: string;
  name: string | null;
}

export interface ResolvedOpener {
  kind: OpenerKind;
  /** The arche's name as the person knows it. */
  name: string;
  /** Where "Return to …" goes. */
  path: string;
  /** Where a sent file lands, in the person's words. */
  destination: string;
  /** Why nothing can be sent there yet, or null. */
  cannotReceive: string | null;
  /** Move the file there; resolves with the name it landed as. */
  receive: (file: OfferedFile) => Promise<{ name: string }>;
}

const FORGE_ARCHE_ID = 'arche_forge';
const BUILDER_ARCHE_ID = 'arche_plugin_builder';

async function resolve(ref: OpenerRef): Promise<ResolvedOpener> {
  if (ref.kind === 'forge') {
    const { forge } = await api.getForge(ref.id);
    const name = forge.name?.trim() || 'your Fias Forge';
    return {
      kind: 'forge',
      name,
      path: `/a/${FORGE_ARCHE_ID}/${ref.id}`,
      destination: `${name}’s files (public/)`,
      cannotReceive: null,
      receive: async (file) => {
        const { delivery } = await api.sendArcheFileToForge(ref.id, {
          ...(file.holderId ? { holderId: file.holderId } : {}),
          archeId: file.sourceArcheId,
          documentId: file.documentId,
          ...(file.name ? { name: file.name } : {}),
        });
        return { name: delivery.files[0]?.name ?? file.name ?? 'file' };
      },
    };
  }
  const { session } = (await api.getPluginBuilderSession(ref.id)) as {
    session: { name?: string | null; archId?: string | null };
  };
  const name = session.name?.trim() || 'your arche';
  const archeId = session.archId ?? null;
  return {
    kind: 'builder',
    name,
    path: `/a/${BUILDER_ARCHE_ID}/${ref.id}`,
    destination: `${name}’s asset library`,
    cannotReceive: archeId
      ? null
      : `Publish ${name} once in the Arche Builder; then you can send pictures to it.`,
    receive: async (file) => {
      if (!archeId) throw new Error(`Publish ${name} once before sending pictures to it.`);
      const { asset } = await api.sendArcheFileToAssetLibrary(archeId, {
        ...(file.holderId ? { holderId: file.holderId } : {}),
        sourceArcheId: file.sourceArcheId,
        documentId: file.documentId,
        ...(file.name ? { name: file.name } : {}),
      });
      return { name: asset.name };
    },
  };
}

/**
 * Resolved openers, briefly, per tab — the arche page mounts the renderer
 * wherever an arche runs (including embeds and previews with no query client),
 * so this is a plain fetch with a short cache rather than React Query.
 */
const RESOLVED_TTL_MS = 5 * 60_000;
const resolved = new Map<string, { at: number; value: Promise<ResolvedOpener> }>();

function resolveCached(ref: OpenerRef): Promise<ResolvedOpener> {
  const key = `${ref.kind}:${ref.id}`;
  const hit = resolved.get(key);
  if (hit && Date.now() - hit.at < RESOLVED_TTL_MS) return hit.value;
  const value = resolve(ref);
  resolved.set(key, { at: Date.now(), value });
  value.catch(() => resolved.delete(key));
  return value;
}

/** Test seam: forget every resolved opener. */
export function _resetArcheOpenerCacheForTests(): void {
  resolved.clear();
}

export function useArcheOpener(archeId: string): ResolvedOpener | null {
  const location = useLocation();
  const [ref, setRef] = useState<OpenerRef | null>(() => recallOpener(archeId));
  const [opener, setOpener] = useState<ResolvedOpener | null>(null);

  // A link that names an opener sets it for this arche, in this tab.
  useEffect(() => {
    const fromUrl = parseOpener(new URLSearchParams(location.search).get(OPENER_PARAM));
    if (fromUrl) rememberOpener(archeId, fromUrl);
    setRef(fromUrl ?? recallOpener(archeId));
  }, [archeId, location.search]);

  const refKey = ref ? `${ref.kind}:${ref.id}` : null;
  useEffect(() => {
    setOpener(null);
    const current = parseOpener(refKey);
    if (!current) return;
    let live = true;
    resolveCached(current).then(
      (value) => live && setOpener(value),
      // Not the person's own (or gone): forget it, and name nothing.
      () => live && forgetOpener(archeId),
    );
    return () => {
      live = false;
    };
  }, [archeId, refKey]);

  return opener;
}
